// =============================================================================
// A aba Pendências de Ferramentas (066, 068). O Painel Financeiro está em
// FerramentasPainel.tsx.
//
// Pendências é a fila: as da planilha, as da tela, as Atenções críticas (que
// eram um bloco da aba Instruções) e as cobranças sem ferramenta (que eram a
// aba "Novas dos CSVs"; a parte 2 vai abri-las sozinha, a partir das cobranças
// do Conta Azul).
// =============================================================================

import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ensinarCobranca, FalhaApi, reconhecerCobrancas, salvarPendenciaFerramenta,
  type CandidataFerramenta, type DadosFerramentas, type Ferramenta, type PendenciaEdicao, type PendenciaFerramenta,
} from "../lib/api.ts";
import { Painel, Vazio } from "../componentes/ui.tsx";
import { CaixaGrade, CLASSE_BOTAO_BARRA } from "../componentes/Grade.tsx";
import { Modal } from "../componentes/Modal.tsx";

const URGENCIA: Record<NonNullable<PendenciaFerramenta["urgencia"]>, string> = {
  critico: "🔴 CRÍTICO", urgente: "🟠 Urgente", medio: "🟡 Médio", baixo: "🔵 Baixo",
};

export function Faixa({ children }: { children: string }) {
  return (
    <p className="mb-2 rounded-lg bg-azul px-3 py-2 text-[11.5px] font-bold tracking-[0.1em] text-white uppercase">{children}</p>
  );
}

export const TH = "border-b border-linha px-3 py-2 whitespace-nowrap";
export const TD = "border-b border-linha px-3 py-1.5 align-top";

// ── ⚠️ Pendências ───────────────────────────────────────────────────────────

/** A Atenção crítica da planilha não tem urgência escrita: é crítica pelo nome. */
const urgenciaDe = (p: PendenciaFerramenta) => p.urgencia ?? (p.secao === "atencoes_criticas" ? "critico" : null);
/** Crítico primeiro; sem urgência, por último. */
const pesoUrgencia = (p: PendenciaFerramenta) => ({ critico: 0, urgente: 1, medio: 2, baixo: 3 })[urgenciaDe(p) ?? "baixo"] + (urgenciaDe(p) ? 0 : 1);

/** O tipo da pendência automática, como aparece embaixo do nome (070). */
const ROTULO_TIPO: Partial<Record<PendenciaFerramenta["tipo"], string>> = {
  cobranca_sem_ferramenta: "cobrança sem ferramenta",
  valor_mudou: "valor mudou",
  cobranca_cancelada: "cobrança de ferramenta cancelada",
  cobranca_ausente: "cobrança não veio",
  renovacao: "renovação",
  termino: "término previsto",
};
const BOTAO = "rounded border border-linha bg-superficie px-2 py-0.5 text-[11.5px] font-semibold hover:border-laranja disabled:opacity-50";

export function AbaPendencias({ dados, onCadastrar, onCadastrarFornecedor, onEditar }: {
  dados: DadosFerramentas;
  onCadastrar: (c: CandidataFerramenta) => void;
  /** Cadastrar a ferramenta de um fornecedor que o Nexa achou nas cobranças (070). */
  onCadastrarFornecedor: (chave: string) => void;
  onEditar: (f: Ferramenta) => void;
}) {
  const qc = useQueryClient();
  const [editando, setEditando] = useState<PendenciaFerramenta | "nova" | null>(null);
  // Abertas antes das resolvidas; dentro delas, a mais urgente primeiro.
  const linhas = useMemo(() => [...dados.pendencias].sort((a, b) =>
    Number(a.resolvido) - Number(b.resolvido) || pesoUrgencia(a) - pesoUrgencia(b)), [dados.pendencias]);
  const candidatas = useMemo(() => new Map(dados.candidatas.map((c) => [c.id, c])), [dados.candidatas]);
  const porId = useMemo(() => new Map(dados.ferramentas.map((f) => [f.id, f])), [dados.ferramentas]);
  const atualizar = () => void qc.invalidateQueries({ queryKey: ["ferramentas"] });
  const alternar = useMutation({
    mutationFn: (p: PendenciaFerramenta) => salvarPendenciaFerramenta({ ...p, resolvido: !p.resolvido }),
    onSuccess: atualizar,
  });
  const ensinar = useMutation({ mutationFn: ensinarCobranca, onSuccess: atualizar });
  const reconhecer = useMutation({ mutationFn: reconhecerCobrancas, onSuccess: atualizar });
  const erro = [alternar, ensinar, reconhecer].find((m) => m.isError)?.error as FalhaApi | undefined;

  return (
    <>
      <Faixa>Pendências e ações necessárias</Faixa>
      <div className="mb-2 flex flex-wrap items-center justify-end gap-2">
        <span className="mr-auto text-[12px] text-tinta-fraca">
          As cobranças do Conta Azul são reconhecidas toda madrugada, e as pendências automáticas abrem sozinhas.
        </span>
        <button type="button" disabled={reconhecer.isPending} onClick={() => reconhecer.mutate()} className={CLASSE_BOTAO_BARRA}
          title="O mesmo que o Nexa faz toda madrugada: liga as cobranças às ferramentas e abre as pendências do último mês fechado.">
          {reconhecer.isPending ? "Reconhecendo…" : "Reconhecer agora"}
        </button>
        <button type="button" onClick={() => setEditando("nova")} className="rounded-lg bg-laranja px-3 py-1.5 text-[12.5px] font-bold text-azul">
          + Nova pendência
        </button>
      </div>
      {erro && <p className="mb-2 text-[12.5px] text-risco-tinta">{erro.message}</p>}
      {linhas.length === 0 ? (
        <Painel><Vazio titulo="Nenhuma pendência" dica="Importe a planilha ou acrescente uma aqui." /></Painel>
      ) : (
        <CaixaGrade>
          <table className="w-full border-separate border-spacing-0 text-[12.5px]">
            <thead className="sticky top-0 z-10 bg-superficie-2 text-left text-[11px] font-semibold tracking-wide text-tinta-fraca">
              <tr>
                <th className={TH}>Ferramenta</th><th className={TH}>Urgência</th><th className={TH}>Motivo da Pendência</th>
                <th className={TH}>Ação Sugerida</th><th className={TH}>Prazo</th><th className={TH}>Responsável</th>
                <th className={`${TH} text-center`}>Resolvido?</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((p) => {
                const urg = urgenciaDe(p);
                const candidata = p.candidata_id ? candidatas.get(p.candidata_id) : undefined;
                return (
                  <tr key={p.id} onClick={() => setEditando(p)} className={`cursor-pointer hover:bg-superficie-2/60 ${p.resolvido ? "opacity-55" : ""}`}>
                    <td className={`${TD} min-w-[180px] font-semibold text-tinta`}>
                      {p.ferramenta_texto}
                      {ROTULO_TIPO[p.tipo] && (
                        <span className="mt-0.5 block text-[11px] font-normal text-tinta-fraca">{ROTULO_TIPO[p.tipo]}</span>
                      )}
                    </td>
                    <td className={`${TD} whitespace-nowrap`}>{urg ? URGENCIA[urg] : ""}</td>
                    <td className={`${TD} min-w-[260px]`}>{p.motivo}</td>
                    <td className={`${TD} min-w-[260px]`}>
                      {p.acao_sugerida}
                      {p.tipo === "cobranca_sem_ferramenta" && !p.resolvido && (
                        <span className="mt-1.5 flex flex-wrap gap-1.5" onClick={(e) => e.stopPropagation()}>
                          {candidata && <button type="button" onClick={() => onCadastrar(candidata)} className={BOTAO}>Cadastrar</button>}
                          {p.chave_cobranca && (
                            <>
                              <button type="button" onClick={() => onCadastrarFornecedor(p.chave_cobranca!)} className={BOTAO}>Cadastrar</button>
                              <EDaFerramenta ferramentas={dados.ferramentas} ocupado={ensinar.isPending}
                                onEscolher={(id) => ensinar.mutate({ chave: p.chave_cobranca!, ferramenta_id: id })} />
                            </>
                          )}
                          <button type="button" disabled={alternar.isPending || ensinar.isPending} className={BOTAO}
                            title={p.chave_cobranca ? "O Nexa deixa de olhar as cobranças deste fornecedor." : undefined}
                            onClick={() => (p.chave_cobranca
                              ? ensinar.mutate({ chave: p.chave_cobranca, nao_e_ferramenta: true })
                              : alternar.mutate(p))}>
                            Não é ferramenta
                          </button>
                        </span>
                      )}
                      {p.tipo !== "cobranca_sem_ferramenta" && !p.resolvido && p.ferramenta_id && porId.get(p.ferramenta_id) && (
                        <span className="mt-1.5 flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                          <button type="button" onClick={() => onEditar(porId.get(p.ferramenta_id!)!)} className={BOTAO}>Abrir ferramenta</button>
                        </span>
                      )}
                    </td>
                    <td className={`${TD} whitespace-nowrap`}>{p.prazo}</td>
                    <td className={TD}>{p.responsavel}</td>
                    <td className={`${TD} text-center`} onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={p.resolvido} disabled={alternar.isPending}
                        aria-label={`Resolvido: ${p.ferramenta_texto}`} onChange={() => alternar.mutate(p)} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CaixaGrade>
      )}
      {editando && (
        <ModalPendencia
          pendencia={editando === "nova" ? null : editando}
          ferramentas={dados.ferramentas}
          onFechar={() => setEditando(null)}
          onSalvo={() => { setEditando(null); void qc.invalidateQueries({ queryKey: ["ferramentas"] }); }}
        />
      )}
    </>
  );
}

/**
 * "É da ferramenta…": o Financeiro diz de qual ferramenta é o fornecedor, e o
 * Nexa passa a reconhecer as próximas cobranças dele sozinho (070).
 */
function EDaFerramenta({ ferramentas, ocupado, onEscolher }: {
  ferramentas: Ferramenta[];
  ocupado: boolean;
  onEscolher: (id: string) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const [id, setId] = useState("");
  const opcoes = useMemo(() => [...ferramentas].filter((f) => f.status !== "cancelada")
    .sort((a, b) => a.nome.localeCompare(b.nome)), [ferramentas]);
  if (!aberto) return <button type="button" onClick={() => setAberto(true)} className={BOTAO}>É da ferramenta…</button>;
  return (
    <span className="flex items-center gap-1">
      <select value={id} onChange={(e) => setId(e.target.value)} aria-label="Ferramenta"
        className="max-w-[200px] rounded border border-linha bg-superficie px-1.5 py-0.5 text-[11.5px]">
        <option value="">Escolha…</option>
        {opcoes.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
      </select>
      <button type="button" disabled={!id || ocupado} onClick={() => onEscolher(id)} className={BOTAO}>Confirmar</button>
      <button type="button" onClick={() => setAberto(false)} className="text-[11.5px] text-tinta-fraca">cancelar</button>
    </span>
  );
}

function ModalPendencia({ pendencia, ferramentas, onFechar, onSalvo }: {
  pendencia: PendenciaFerramenta | null;
  ferramentas: Ferramenta[];
  onFechar: () => void;
  onSalvo: () => void;
}) {
  const [p, setP] = useState<PendenciaEdicao>(() => pendencia
    ? { id: pendencia.id, secao: pendencia.secao, ferramenta_id: pendencia.ferramenta_id, ferramenta_texto: pendencia.ferramenta_texto,
        urgencia: urgenciaDe(pendencia), motivo: pendencia.motivo, acao_sugerida: pendencia.acao_sugerida, prazo: pendencia.prazo,
        responsavel: pendencia.responsavel, resolvido: pendencia.resolvido }
    : { secao: "pendencias", ferramenta_id: null, ferramenta_texto: "", urgencia: "medio", motivo: null,
        acao_sugerida: null, prazo: null, responsavel: null, resolvido: false });
  const set = <K extends keyof PendenciaEdicao>(k: K, v: PendenciaEdicao[K]) => setP((x) => ({ ...x, [k]: v }));
  const txt = (v: string) => (v.trim() ? v : null);
  const nomes = useMemo(() => [...new Set(ferramentas.map((f) => f.nome))].sort((a, b) => a.localeCompare(b)), [ferramentas]);
  const salvar = useMutation({
    mutationFn: () => {
      // Ligada à ferramenta quando o nome é exatamente o de uma só.
      const iguais = ferramentas.filter((f) => f.nome.trim().toLowerCase() === p.ferramenta_texto.trim().toLowerCase());
      return salvarPendenciaFerramenta({ ...p, ferramenta_texto: p.ferramenta_texto.trim(), ferramenta_id: iguais.length === 1 ? iguais[0]!.id : null });
    },
    onSuccess: onSalvo,
  });
  const campo = "mt-1 w-full rounded-lg border border-linha bg-superficie px-3 py-2 text-[13.5px]";
  const rot = "text-[12px] font-semibold text-tinta-media";

  return (
    <Modal titulo={pendencia ? pendencia.ferramenta_texto : "Nova pendência"} onFechar={onFechar} largura="max-w-2xl">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block sm:col-span-2"><span className={rot}>Ferramenta *</span>
          <input value={p.ferramenta_texto} onChange={(e) => set("ferramenta_texto", e.target.value)} list="pend-ferramentas" className={campo} />
          <datalist id="pend-ferramentas">{nomes.map((n) => <option key={n} value={n} />)}</datalist></label>
        <label className="block"><span className={rot}>Urgência</span>
          <select value={p.urgencia ?? ""} onChange={(e) => set("urgencia", (e.target.value || null) as PendenciaEdicao["urgencia"])} className={campo}>
            <option value="">—</option>
            {Object.entries(URGENCIA).map(([v, r]) => <option key={v} value={v}>{r}</option>)}
          </select></label>
        <label className="block"><span className={rot}>Prazo</span>
          <input value={p.prazo ?? ""} onChange={(e) => set("prazo", txt(e.target.value))} className={campo} /></label>
        <label className="block sm:col-span-2"><span className={rot}>Motivo da Pendência</span>
          <textarea value={p.motivo ?? ""} onChange={(e) => set("motivo", txt(e.target.value))} rows={3} className={campo} /></label>
        <label className="block sm:col-span-2"><span className={rot}>Ação Sugerida</span>
          <textarea value={p.acao_sugerida ?? ""} onChange={(e) => set("acao_sugerida", txt(e.target.value))} rows={2} className={campo} /></label>
        <label className="block"><span className={rot}>Responsável</span>
          <input value={p.responsavel ?? ""} onChange={(e) => set("responsavel", txt(e.target.value))} className={campo} /></label>
        <label className="flex items-center gap-2 pt-6 text-[13px]">
          <input type="checkbox" checked={p.resolvido} onChange={(e) => set("resolvido", e.target.checked)} /> Resolvido
        </label>
      </div>
      {salvar.isError && <p className="mt-3 text-[12.5px] text-risco-tinta">{(salvar.error as FalhaApi).message}</p>}
      <div className="mt-5 flex justify-end gap-2">
        <button type="button" onClick={onFechar} className={CLASSE_BOTAO_BARRA}>Cancelar</button>
        <button type="button" disabled={!p.ferramenta_texto.trim() || salvar.isPending} onClick={() => salvar.mutate()}
          className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:opacity-45">
          {salvar.isPending ? "Salvando…" : "Salvar"}
        </button>
      </div>
    </Modal>
  );
}
